/**
 * Process View — the plant as a live schematic.
 *
 * A process-mimic screen for Shomrat Gadol (Ozone Nano Bubbles): the real
 * three-train ozone plant drawn as a schematic with LIVE Zeus values overlaid
 * directly on the equipment — flow on each train, ozone-generator state,
 * pressure at each sensor, and the shared dissolved-oxygen reading at the
 * outlet. Values are colour-coded by role (measured / state / setpoint) and
 * anything Zeus does not report (PT-203) shows honest N/A.
 *
 * Data: /api/zeus/last-known-values/0U5B3XY4 (15 s poll) — the same scoped,
 * read-only endpoint the overview page uses; values are keyed by channel.
 * Channel map verified live 2026-07-12 (GetLastKnownValuesExtended):
 *   0/1/2   FM-102/202/302  flow per train (m³/h)
 *   53/54/55 PT-101/102/103 pressure per train (bar)
 *   26/48/51 OZ-105/205/305 ozone-generator output (on ≥ 1)
 *   30 dissolved O₂ (mg/L) · 29 DO temp (°C) · 3 PT-203 (reads 0 → N/A)
 */

import { useMemo, type CSSProperties } from "react";
import { useQuery } from "@tanstack/react-query";
import { CheckCircle2, Radio, Zap } from "lucide-react";
import { useApi } from "@/lib/api";

const PILOT_STATION_ID = "0U5B3XY4";
const PROCESS_NAME = "Shomrat Gadol — Ozone Nano Bubbles";
const PROCESS_SUBTITLE =
  "HERMES_M103 · 3-train O₃ nano-bubble treatment · Water / SCADA";
const LIVE_REFETCH_MS = 15_000;

// Channel map (verified live 2026-07-12).
const CH_DO = 30;
const CH_DO_TEMP = 29;
const CH_PT203 = 3;
const CH_P101_OL = 22;

interface LiveResp {
  stationId: string;
  stationLabel: string | null;
  lastConnectionTime: string | null;
  rssi: number | null;
  powerSupply: string | null;
  values: Record<string, number>;
  servedFromCache: boolean;
  fetchedAt: string;
}

interface Train {
  n: number;
  flow: number; // channel
  pt: number;
  oz: number;
  fmName: string;
  pName: string;
  ozName: string;
  ptName: string;
}

const TRAINS: Train[] = [
  { n: 1, flow: 0, pt: 53, oz: 26, fmName: "FM-102", pName: "P-101", ozName: "OZ-105", ptName: "PT-101" },
  { n: 2, flow: 1, pt: 54, oz: 48, fmName: "FM-202", pName: "P-201", ozName: "OZ-205", ptName: "PT-102" },
  { n: 3, flow: 2, pt: 55, oz: 51, fmName: "FM-302", pName: "P-301", ozName: "OZ-305", ptName: "PT-103" },
];

const C = {
  ink: "#1C1917",
  ink2: "#57534E",
  mut: "#A8A29E",
  line: "#E7E5E4",
  pipe: "#D6DCE4",
  measured: "#3B82C8",
  measuredBorder: "#BFD3EE",
  green: "#16A34A",
  greenBg: "#ECFDF3",
  greenBorder: "#BBE9C9",
  amber: "#D97706",
  amberBorder: "#F0D28A",
  water: "#BFD8F2",
};

function useLive() {
  const api = useApi();
  return useQuery<LiveResp>({
    queryKey: ["zeus-live", PILOT_STATION_ID, "process"],
    queryFn: async () => {
      const res = await api(`/api/zeus/last-known-values/${PILOT_STATION_ID}`);
      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(`HTTP ${res.status}: ${text || res.statusText}`);
      }
      return res.json();
    },
    refetchInterval: LIVE_REFETCH_MS,
    refetchIntervalInBackground: true,
    staleTime: 0,
  });
}

function num(v: number | null | undefined, digits = 2): string {
  if (v == null || !Number.isFinite(v)) return "—";
  return v.toLocaleString(undefined, { maximumFractionDigits: digits, minimumFractionDigits: digits === 0 ? 0 : 1 });
}

// ─── SVG chip primitives ─────────────────────────────────────────────────
function Chip({
  x, y, label, value, unit, tone = "measured",
}: {
  x: number; y: number; label: string; value: string; unit?: string;
  tone?: "measured" | "setpoint" | "offline";
}) {
  const border = tone === "measured" ? C.measuredBorder : C.amberBorder;
  const labelColor = tone === "measured" ? C.measured : C.amber;
  const dashed = tone !== "measured";
  return (
    <g transform={`translate(${x},${y})`}>
      <rect
        width={104} height={38} rx={9}
        fill="#fff" stroke={border} strokeWidth={1.4}
        strokeDasharray={dashed ? "4 3" : undefined}
        style={{ filter: "drop-shadow(0 2px 4px rgba(28,25,23,0.08))" }}
      />
      <text x={10} y={15} fontSize={8.5} fontWeight={800} letterSpacing="0.04em" fill={labelColor}>
        {label.toUpperCase()}
      </text>
      <text x={10} y={30} fontSize={15} fontWeight={800} fill={C.ink}>
        {value}
        {unit && <tspan fontSize={9.5} fontWeight={600} fill={C.mut}> {unit}</tspan>}
      </text>
    </g>
  );
}

function StatePill({ x, y, text, on }: { x: number; y: number; text: string; on: boolean }) {
  return (
    <g transform={`translate(${x},${y})`}>
      <rect
        width={92} height={26} rx={13}
        fill={on ? C.greenBg : "#F5F5F4"}
        stroke={on ? C.greenBorder : C.line}
        strokeWidth={1.4}
      />
      <circle cx={16} cy={13} r={4} fill={on ? C.green : C.mut} />
      <text x={28} y={17} fontSize={11.5} fontWeight={800} fill={on ? "#15803D" : C.ink2}>
        {text}
      </text>
    </g>
  );
}

// pump symbol (impeller) — green ring when running
function Pump({ x, y, running, label }: { x: number; y: number; running: boolean; label: string }) {
  const col = running ? C.green : C.mut;
  return (
    <g transform={`translate(${x},${y})`}>
      <circle r={26} fill={running ? C.greenBg : "#fff"} stroke={col} strokeWidth={2.5} />
      <path d="M0,0 L0,-17 M0,0 L15,10 M0,0 L-15,10" stroke={col} strokeWidth={2.6} fill="none" strokeLinecap="round" />
      <circle r={3.4} fill={col} />
      <text x={0} y={44} textAnchor="middle" fontSize={11} fontWeight={700} fill={C.ink2}>{label}</text>
    </g>
  );
}

export function ProcessViewPage() {
  const live = useLive();
  const values = live.data?.values;

  const val = (ch: number): number | null => {
    const x = values?.[String(ch)];
    return typeof x === "number" && Number.isFinite(x) ? x : null;
  };

  const totals = useMemo(() => {
    const flows = TRAINS.map((t) => val(t.flow)).filter((v): v is number => v != null);
    const press = TRAINS.map((t) => val(t.pt)).filter((v): v is number => v != null && v > 0);
    const ozOn = TRAINS.filter((t) => (val(t.oz) ?? 0) >= 1).length;
    return {
      totalFlow: flows.length ? flows.reduce((a, b) => a + b, 0) : null,
      avgPressure: press.length ? press.reduce((a, b) => a + b, 0) / press.length : null,
      ozOn,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values]);

  const doVal = val(CH_DO);
  const doTemp = val(CH_DO_TEMP);
  const pt203 = val(CH_PT203);
  const p101ol = val(CH_P101_OL);
  const connected = !!live.data && !live.isError;

  // schematic geometry
  const rowY = [140, 300, 460];
  const X = { intake: 96, pump: 250, oz: 470, pt: 700, merge: 880, doBlk: 980, out: 1120 };

  const kpis = [
    { label: "Total flow", value: num(totals.totalFlow, 1), unit: "m³/h", accent: C.measured },
    { label: "Avg pressure", value: num(totals.avgPressure), unit: "bar", accent: C.green },
    { label: "Dissolved O₂", value: num(doVal), unit: "mg/L", accent: "#6366F1" },
    { label: "Ozone trains on", value: `${totals.ozOn}/3`, unit: "", accent: C.amber },
  ];

  return (
    <div className="mx-auto flex w-full max-w-6xl flex-col gap-5 px-1 py-2">
      {/* header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-stone-400">
            Engineering Dashboard · Process View
          </p>
          <h1 className="mt-1 text-2xl font-extrabold tracking-tight text-stone-900">{PROCESS_NAME}</h1>
          <p className="mt-1 text-xs text-stone-500">
            {PROCESS_SUBTITLE}
            {live.data?.lastConnectionTime && ` · last sync ${live.data.lastConnectionTime}`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <StatusBadge on={connected} icon={CheckCircle2} text={connected ? "PLC connected" : "Reconnecting…"} />
          {live.data?.powerSupply && <StatusBadge on icon={Zap} text={live.data.powerSupply} />}
          {live.data?.rssi != null && <StatusBadge on icon={Radio} text={`RSSI ${live.data.rssi}`} />}
        </div>
      </div>

      {/* error banner */}
      {live.isError && (
        <div className="rounded-lg border-l-4 border-red-500 bg-red-50 px-4 py-3 text-sm text-red-700">
          Live telemetry unavailable — {(live.error as Error)?.message ?? "unknown error"}
        </div>
      )}

      {/* KPI strip */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        {kpis.map((k) => (
          <div key={k.label} className="flex items-center justify-between rounded-xl border bg-card px-4 py-3 shadow-sm">
            <div>
              <p className="text-xs font-semibold text-stone-500">{k.label}</p>
              <p className="mt-0.5 text-2xl font-extrabold tracking-tight text-stone-900">
                {k.value}
                {k.unit && <span className="ml-1 text-xs font-semibold text-stone-400">{k.unit}</span>}
              </p>
            </div>
            <span className="h-2.5 w-2.5 rounded-full" style={{ background: k.accent, boxShadow: `0 0 0 4px ${k.accent}22` }} />
          </div>
        ))}
      </div>

      {/* schematic */}
      <div className="rounded-2xl border bg-card p-4 shadow-sm md:p-5">
        <div className="mb-1 flex items-center justify-between">
          <div>
            <h2 className="text-sm font-bold text-stone-900">Process schematic — live</h2>
            <p className="text-[11px] font-medium text-stone-400">values read from Zeus, overlaid on the plant · refreshes every 15s</p>
          </div>
        </div>

        <svg viewBox="0 0 1180 600" className="h-auto w-full" style={{ fontFamily: "Inter, system-ui, sans-serif" }}>
          <defs>
            <linearGradient id="pv-w" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0" stopColor="#DCEAFB" />
              <stop offset="1" stopColor={C.water} />
            </linearGradient>
            <marker id="pv-arw" markerWidth="9" markerHeight="9" refX="5" refY="3" orient="auto">
              <path d="M0,0 L6,3 L0,6 Z" fill="#93A4BC" />
            </marker>
          </defs>

          {/* common intake reservoir */}
          <rect x={40} y={228} width={70} height={144} rx={9} fill="#F1F5F9" stroke={C.pipe} strokeWidth={2} />
          <rect x={46} y={292} width={58} height={74} rx={6} fill="url(#pv-w)" />
          <text x={75} y={392} textAnchor="middle" fontSize={11} fontWeight={700} fill={C.ink2}>Raw water</text>
          <text x={75} y={406} textAnchor="middle" fontSize={9.5} fontWeight={600} fill={C.mut}>intake</text>

          {/* per-train pipework + equipment */}
          {TRAINS.map((t, i) => {
            const y = rowY[i];
            const ozOn = (val(t.oz) ?? 0) >= 1;
            const flow = val(t.flow);
            const press = val(t.pt);
            return (
              <g key={t.n}>
                {/* branch from intake to train, then the train pipe */}
                <path
                  d={`M110,300 C${X.intake + 40},300 ${X.intake + 40},${y} ${X.pump},${y} H${X.merge}`}
                  fill="none" stroke={C.pipe} strokeWidth={11} strokeLinecap="round"
                />
                <path
                  d={`M110,300 C${X.intake + 40},300 ${X.intake + 40},${y} ${X.pump},${y} H${X.merge}`}
                  fill="none" stroke="#EEF2F7" strokeWidth={3.5} strokeLinecap="round"
                />
                {/* flow arrow */}
                <line x1={X.pump + 55} y1={y} x2={X.pump + 72} y2={y} stroke="#93A4BC" strokeWidth={2} markerEnd="url(#pv-arw)" />
                <line x1={X.oz + 120} y1={y} x2={X.oz + 137} y2={y} stroke="#93A4BC" strokeWidth={2} markerEnd="url(#pv-arw)" />

                {/* train label */}
                <text x={X.pump - 42} y={y - 42} fontSize={12} fontWeight={800} fill={C.ink}>Train {t.n}</text>

                {/* pump */}
                <Pump x={X.pump} y={y} running={flow != null && flow > 0.5} label={t.pName} />

                {/* ozone generator box */}
                <rect x={X.oz} y={y - 34} width={118} height={68} rx={11}
                  fill={ozOn ? C.greenBg : "#FafafA"} stroke={ozOn ? C.greenBorder : C.line} strokeWidth={2} />
                <text x={X.oz + 59} y={y - 12} textAnchor="middle" fontSize={11} fontWeight={800} fill={C.ink}>{t.ozName}</text>
                <text x={X.oz + 59} y={y + 2} textAnchor="middle" fontSize={8.5} fontWeight={700} fill={C.mut}>O₃ GENERATOR</text>
                <StatePill x={X.oz + 13} y={y + 10} text={ozOn ? "ON" : "OFF"} on={ozOn} />

                {/* pressure sensor tap */}
                <circle cx={X.pt} cy={y} r={6} fill="#fff" stroke={C.measured} strokeWidth={2.4} />
                <circle cx={X.pt} cy={y} r={2} fill={C.measured} />

                {/* chips */}
                <Chip x={X.pump - 52} y={y - 96} label={t.fmName} value={num(flow, 2)} unit="m³/h" />
                <Chip x={X.pt - 52} y={y + 22} label={t.ptName} value={num(press, 2)} unit="bar"
                  tone={press != null && press > 0 ? "measured" : "offline"} />
              </g>
            );
          })}

          {/* converge manifold → DO block → outlet */}
          <path d={`M${X.merge},140 V460`} fill="none" stroke={C.pipe} strokeWidth={11} strokeLinecap="round" />
          <path d={`M${X.merge},300 H${X.doBlk}`} fill="none" stroke={C.pipe} strokeWidth={11} strokeLinecap="round" />
          <rect x={X.doBlk} y={266} width={96} height={68} rx={11} fill="#EEF0FF" stroke="#C9CBF5" strokeWidth={2} />
          <text x={X.doBlk + 48} y={292} textAnchor="middle" fontSize={10.5} fontWeight={800} fill={C.ink}>Dissolved O₂</text>
          <text x={X.doBlk + 48} y={306} textAnchor="middle" fontSize={8.5} fontWeight={700} fill={C.mut}>DO-403 · OUTLET</text>
          {/* outlet pipe + treated water arrow */}
          <path d={`M${X.doBlk + 96},300 H${X.out} v40`} fill="none" stroke={C.pipe} strokeWidth={11} strokeLinecap="round" />
          <line x1={X.out} y1={340} x2={X.out} y2={366} stroke="#93A4BC" strokeWidth={2.4} markerEnd="url(#pv-arw)" />
          <text x={X.out} y={392} textAnchor="middle" fontSize={11} fontWeight={700} fill={C.ink2}>Treated</text>
          <text x={X.out} y={406} textAnchor="middle" fontSize={9.5} fontWeight={600} fill={C.mut}>water out</text>

          {/* shared DO chips */}
          <Chip x={X.doBlk} y={196} label="DO · O₂" value={num(doVal)} unit="mg/L" />
          <Chip x={X.doBlk + 4} y={352} label="DO temp" value={num(doTemp, 1)} unit="°C" />
          {/* PT-203 honest N/A — top-right, clear of the DO chips */}
          <Chip x={X.out - 70} y={70}
            label="PT-203"
            value={pt203 === 0 ? "N/A" : num(pt203)}
            unit={pt203 === 0 ? undefined : "bar"}
            tone="offline" />
          {pt203 === 0 && (
            <text x={X.out - 18} y={124} textAnchor="middle" fontSize={9} fontWeight={700} fill={C.amber}>
              field check
            </text>
          )}
        </svg>

        {/* legend */}
        <div className="mt-1 flex flex-wrap items-center gap-x-6 gap-y-2 border-t pt-3 text-[11px] font-semibold text-stone-500">
          <LegendSwatch style={{ border: `1.4px solid ${C.measuredBorder}` }} label="Measured value" />
          <LegendSwatch style={{ border: `1.4px dashed ${C.amberBorder}`, background: "#FFFBEB" }} label="Offline / N/A" />
          <LegendSwatch style={{ border: `1.4px solid ${C.greenBorder}`, background: C.greenBg }} label="Running / OK" />
          {p101ol === 0 && (
            <span className="inline-flex items-center gap-1.5 text-emerald-600">
              <CheckCircle2 className="h-3.5 w-3.5" /> P-101 no overload
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

function StatusBadge({ on, icon: Icon, text }: { on: boolean; icon: typeof CheckCircle2; text: string }) {
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-lg border bg-stone-50 px-2.5 py-1.5 text-[11px] font-bold text-stone-600"
    >
      <Icon className="h-3.5 w-3.5" style={{ color: on ? C.green : C.amber }} />
      {text}
    </span>
  );
}

function LegendSwatch({ style, label }: { style: CSSProperties; label: string }) {
  return (
    <span className="inline-flex items-center gap-2">
      <span className="inline-block h-3.5 w-5 rounded" style={{ background: "#fff", ...style }} />
      {label}
    </span>
  );
}

export default ProcessViewPage;
